import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { COOKIE, UTILS } from "../../constants/apis";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useLabels from "../../hooks/useLabels";
import { ActionButton } from "@wine2trace/ui/FormComponents";
import { Icon } from "../Icon";
import LegalTitle from "../AdditionalInfo/LegalTitle";
import CookieBarView from "@wine2trace/ui/CookieBar";

const hasConsentCookie = () => Cookies.get("viewed_cookie_policy") === "yes";

const CookieBar = ({ isBar = true }) => {
  const [getLabel] = useLabels();
  const axiosPrivate = useAxiosPrivate();
  const [isCookieAccepted, setIsCookieAccepted] = useState(true);

  const [isMandatory, setIsMandatory] = useState(true);
  // What the settings page shows: the consent cookie is read again after each request, not at render time.
  const [hasConsent, setHasConsent] = useState(hasConsentCookie);

  useEffect(() => {
    const cookieAccepted = Cookies.get("viewed_cookie_policy");
    if (cookieAccepted === "yes") {
      setIsCookieAccepted(true);
      return;
    }
    setIsCookieAccepted(false);
  }, []);

  const acceptCookie = async () => {
    try {
      const response = await axiosPrivate.post(`${UTILS}${COOKIE}`, {
        withCredentials: true,
      });
      if (response.status === 200) {
        setIsCookieAccepted(true);
        setHasConsent(hasConsentCookie());
      }
    } catch (err) {
      console.error(err);
    }
  };

  const denyCookie = async () => {
    try {
      const response = await axiosPrivate.delete(`${UTILS}${COOKIE}`, {
        withCredentials: true,
      });
      if (response.status === 200) {
        setIsCookieAccepted(false);
        setHasConsent(hasConsentCookie());
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsCookieAccepted(true);
    }
  };

  if (!isBar) {
    const cookies = [
      { name: "jwt", editable: false },
      { name: "viewed_cookie_policy", editable: true },
    ];
    const canWithdraw = hasConsent;
    const text = getLabel("cookie_settings_text");
    const paragraphs = Array.isArray(text) ? text : [text];
    const labels = {
      cookie: getLabel("cookie_settings_cookie"),
      duration: getLabel("cookie_settings_duration"),
      description: getLabel("cookie_settings_description"),
      actions: getLabel("cookie_settings_actions"),
    };

    return (
      <div className="legal_page">
        <article className="legal_card" aria-labelledby="legal-title">
          <LegalTitle id="legal-title" title={getLabel("cookie_settings_title")} />
          <div className="legal_intro">
            {paragraphs.map((paragraph, i) => (
              <p key={`settings-text-${i}`}>{paragraph}</p>
            ))}
          </div>
          <section className="legal_section" aria-labelledby="cookie-settings-mandatory">
            <h2 className="legal_section_title" id="cookie-settings-mandatory">
              <button
                type="button"
                className="legal_toggle"
                aria-expanded={isMandatory}
                aria-controls="cookie-settings-mandatory-content"
                onClick={() => setIsMandatory((prev) => !prev)}
              >
                {getLabel("cookie_settings_mandatory")}
                <Icon icon={isMandatory ? "fa-angles-up" : "fa-angles-down"} size="xs" aria-hidden="true" />
              </button>
            </h2>
            <div id="cookie-settings-mandatory-content">
              {isMandatory && (
                <>
                  <p>{getLabel("cookie_settings_mandatory_description")}</p>
                  <table className="data_table legal_table">
                    <thead>
                      <tr>
                        <th>{labels.cookie}</th>
                        <th>{labels.duration}</th>
                        <th>{labels.description}</th>
                        <th>{labels.actions}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cookies.map(({ name, editable }) => (
                        <tr key={name}>
                          <td data-label={labels.cookie}>
                            <code className="data_cell_main">{name}</code>
                          </td>
                          <td data-label={labels.duration}>
                            {getLabel(`cookie_settings_mandatory_${name}_duration`)}
                          </td>
                          <td data-label={labels.description}>
                            {getLabel(`cookie_settings_mandatory_${name}_description`)}
                          </td>
                          <td data-label={labels.actions}>
                            {editable && canWithdraw ? (
                              <ActionButton onClick={denyCookie}>
                                {getLabel("cookie_settings_mandatory_viewed_cookie_policy_deny")}
                              </ActionButton>
                            ) : (
                              <span aria-hidden="true">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </section>
        </article>
      </div>
    );
  }
  if (isCookieAccepted) return null;

  return (
    <CookieBarView
      onAccept={acceptCookie}
      onDeny={denyCookie}
      onMore={() => window.open("/cookie", "_blank", "rel=noopener noreferrer")}
    />
  );
};

export default CookieBar;
